import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ImageUploader } from "@/components/cms/ImageUploader";

interface SettingsData {
  site_name: string;
  language: string;
  timezone: string;
  currency: string;
  currency_symbol: string;
  logo_url: string | null;
  favicon_url: string | null;
  palette: Record<string, string>;
  contact_email: string | null;
  contact_phone: string | null;
  social_instagram: string | null;
  social_instagram_url: string | null;
  social_tiktok: string | null;
  vat_number: string | null;
  address: string | null;
  contact_eyebrow: string | null;
  contact_title: string | null;
  contact_description: string | null;
  contact_cta_label: string | null;
  seo_title: string;
  seo_description: string;
  privacy_policy: string | null;
  cookie_policy: string | null;
  terms_conditions: string | null;
  request_retention_months: number;
  integrations: { ga4_measurement_id: string | null };
  smtp: {
    host: string | null; port: number; username: string | null; password: string | null;
    use_tls: boolean; from_email: string | null; from_name: string; admin_notification_email: string | null;
  };
}

const TABS = [
  { key: "brand", label: "Brand" },
  { key: "palette", label: "Colori" },
  { key: "contatti", label: "Contatti & SEO" },
  { key: "legale", label: "Legale" },
  { key: "smtp", label: "SMTP" },
  { key: "integrazioni", label: "Integrazioni" },
];

const PALETTE_KEYS = [
  { key: "black", label: "Nero (base)" },
  { key: "white", label: "Bianco (contrasto)" },
  { key: "fuchsia", label: "Fucsia (accento 1)" },
  { key: "lime", label: "Giallo fluo/lime (accento 2)" },
];

export default function SettingsPage() {
  const [data, setData] = useState<SettingsData | null>(null);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const set = (patch: Partial<SettingsData>) => setData((p) => (p ? { ...p, ...patch } : p));
  const setSmtp = (patch: Partial<SettingsData["smtp"]>) =>
    setData((p) => (p ? { ...p, smtp: { ...p.smtp, ...patch } } : p));
  const setPalette = (k: string, v: string) =>
    setData((p) => (p ? { ...p, palette: { ...p.palette, [k]: v } } : p));
  const setIntegrations = (patch: Partial<SettingsData["integrations"]>) =>
    setData((p) => (p ? { ...p, integrations: { ...p.integrations, ...patch } } : p));

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        site_name: data.site_name, language: data.language, timezone: data.timezone,
        currency: data.currency, currency_symbol: data.currency_symbol,
        logo_url: data.logo_url, favicon_url: data.favicon_url, palette: data.palette,
        contact_email: data.contact_email, contact_phone: data.contact_phone,
        social_instagram: data.social_instagram, social_instagram_url: data.social_instagram_url,
        social_tiktok: data.social_tiktok,
        vat_number: data.vat_number, address: data.address,
        contact_eyebrow: data.contact_eyebrow, contact_title: data.contact_title,
        contact_description: data.contact_description, contact_cta_label: data.contact_cta_label,
        seo_title: data.seo_title, seo_description: data.seo_description,
        privacy_policy: data.privacy_policy, cookie_policy: data.cookie_policy,
        terms_conditions: data.terms_conditions,
        request_retention_months: data.request_retention_months,
        integrations: data.integrations,
        smtp: data.smtp,
      });
      toast.success("Impostazioni salvate");
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Inserisci un'email di destinazione."); return; }
    setTesting(true);
    try {
      const { data: res } = await api.post("/admin/smtp/test", { to: testEmail.trim() });
      toast.success(res.message || "Email di test inviata");
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setTesting(false); }
  };

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="settings-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">Sistema</p>
          <h1 className="font-display font-black tracking-tighter text-4xl">Impostazioni</h1>
          <p className="text-muted-foreground text-sm mt-1">Brand, colori, contatti, SEO e SMTP</p>
        </div>
        <button
          data-testid="settings-save-button"
          onClick={save}
          disabled={saving}
          className="shrink-0 flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
        </button>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-8">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`settings-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-secondary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-2xl space-y-6">
        {tab === "brand" && (
          <>
            <Text label="Nome sito" value={data.site_name} onChange={(v) => set({ site_name: v })} testid="settings-site_name" />
            <div className="grid grid-cols-2 gap-4">
              <Text label="Lingua" value={data.language} onChange={(v) => set({ language: v })} testid="settings-language" />
              <Text label="Timezone" value={data.timezone} onChange={(v) => set({ timezone: v })} testid="settings-timezone" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Text label="Valuta" value={data.currency} onChange={(v) => set({ currency: v })} testid="settings-currency" />
              <Text label="Simbolo valuta" value={data.currency_symbol} onChange={(v) => set({ currency_symbol: v })} testid="settings-currency_symbol" />
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Logo</label>
                <ImageUploader value={data.logo_url} onChange={(v) => set({ logo_url: v })} testid="settings-logo" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Favicon</label>
                <ImageUploader value={data.favicon_url} onChange={(v) => set({ favicon_url: v })} testid="settings-favicon" />
              </div>
            </div>
          </>
        )}

        {tab === "palette" && (
          <div className="space-y-4" data-testid="settings-palette">
            <p className="text-sm text-muted-foreground">Solo questi 4 colori, coerenti col logo.</p>
            {PALETTE_KEYS.map((p) => (
              <div key={p.key} className="flex items-center gap-4">
                <input
                  type="color"
                  data-testid={`settings-color-${p.key}`}
                  value={data.palette[p.key] || "#000000"}
                  onChange={(e) => setPalette(p.key, e.target.value)}
                  className="h-12 w-16 bg-transparent border border-white/15 cursor-pointer"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">{p.label}</p>
                  <input
                    value={data.palette[p.key] || ""}
                    onChange={(e) => setPalette(p.key, e.target.value)}
                    className="mt-1 w-40 border border-input bg-black/50 py-1.5 px-3 text-sm outline-none focus:border-primary transition-colors"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "contatti" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Text label="Email contatto" value={data.contact_email ?? ""} onChange={(v) => set({ contact_email: v })} testid="settings-contact_email" />
              <Text label="Telefono" value={data.contact_phone ?? ""} onChange={(v) => set({ contact_phone: v })} testid="settings-contact_phone" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Text label="Instagram (username)" value={data.social_instagram ?? ""} onChange={(v) => set({ social_instagram: v })} testid="settings-instagram" />
              <Text label="Instagram (URL profilo, opzionale)" value={data.social_instagram_url ?? ""} onChange={(v) => set({ social_instagram_url: v })} testid="settings-instagram_url" />
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Inserisci solo lo username (es. <code>bonkercustom</code>): il link viene generato automaticamente. In alternativa specifica l'URL completo del profilo.</p>
            <div className="grid grid-cols-2 gap-4">
              <Text label="TikTok" value={data.social_tiktok ?? ""} onChange={(v) => set({ social_tiktok: v })} testid="settings-tiktok" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Text label="Partita IVA" value={data.vat_number ?? ""} onChange={(v) => set({ vat_number: v })} testid="settings-vat_number" />
              <Text label="Indirizzo" value={data.address ?? ""} onChange={(v) => set({ address: v })} testid="settings-address" />
            </div>

            <div className="border-t border-white/10 pt-6 mt-2 space-y-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Sezione Contatti (pagina pubblica)</p>
              <div className="grid grid-cols-2 gap-4">
                <Text label="Testo sopra il titolo" value={data.contact_eyebrow ?? ""} onChange={(v) => set({ contact_eyebrow: v })} testid="settings-contact_eyebrow" />
                <Text label="Titolo sezione" value={data.contact_title ?? ""} onChange={(v) => set({ contact_title: v })} testid="settings-contact_title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Testo descrittivo</label>
                <textarea
                  data-testid="settings-contact_description"
                  value={data.contact_description ?? ""}
                  onChange={(e) => set({ contact_description: e.target.value })}
                  rows={3}
                  className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
                />
              </div>
              <Text label="Testo del pulsante" value={data.contact_cta_label ?? ""} onChange={(v) => set({ contact_cta_label: v })} testid="settings-contact_cta_label" />
            </div>

            <Text label="SEO Title" value={data.seo_title} onChange={(v) => set({ seo_title: v })} testid="settings-seo_title" />
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">SEO Description</label>
              <textarea
                data-testid="settings-seo_description"
                value={data.seo_description}
                onChange={(e) => set({ seo_description: e.target.value })}
                rows={3}
                className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
              />
            </div>
          </>
        )}

        {tab === "legale" && (
          <>
            <p className="text-sm text-muted-foreground">Testi delle pagine legali, collegate dal footer del sito. Puoi incollare qui il testo completo (privacy, cookie, termini).</p>
            <LegalField label="Privacy Policy" value={data.privacy_policy ?? ""} onChange={(v) => set({ privacy_policy: v })} testid="settings-privacy_policy" />
            <LegalField label="Cookie Policy" value={data.cookie_policy ?? ""} onChange={(v) => set({ cookie_policy: v })} testid="settings-cookie_policy" />
            <LegalField label="Termini e Condizioni" value={data.terms_conditions ?? ""} onChange={(v) => set({ terms_conditions: v })} testid="settings-terms_conditions" />

            <div className="border-t border-white/10 pt-6">
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">Conservazione richieste (mesi)</label>
              <input
                type="number"
                min={0}
                data-testid="settings-retention"
                value={data.request_retention_months ?? 12}
                onChange={(e) => set({ request_retention_months: Math.max(0, Number(e.target.value) || 0) })}
                className="w-40 border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
              />
              <p className="text-xs text-muted-foreground mt-2">Le richieste di preventivo piu' vecchie di questo numero di mesi (con contatti e immagini) vengono eliminate automaticamente ogni giorno. Imposta <strong>0</strong> per disattivare la cancellazione automatica.</p>
            </div>
          </>
        )}

        {tab === "integrazioni" && (
          <>
            <p className="text-sm text-muted-foreground">Integrazioni con servizi esterni. Lascia vuoto per non attivare nulla.</p>
            <Text label="Google Analytics 4 — Measurement ID" value={data.integrations?.ga4_measurement_id ?? ""} onChange={(v) => setIntegrations({ ga4_measurement_id: v.trim() })} testid="settings-ga4_id" />
            <p className="text-xs text-muted-foreground -mt-2">Formato <code>G-XXXXXXXXXX</code>. Se vuoto, Google Analytics non viene caricato. Con un ID valido, GA4 si attiva automaticamente <strong>solo dopo l'accettazione dei cookie</strong> e traccia tutte le pagine, incluse le navigazioni interne del sito.</p>
          </>
        )}

        {tab === "smtp" && (
          <>
            <p className="text-sm text-muted-foreground">Configurazione SMTP per l'invio automatico delle email (conferma cliente + notifica admin) alla ricezione di una richiesta.</p>
            <div className="grid grid-cols-2 gap-4">
              <Text label="Host" value={data.smtp.host ?? ""} onChange={(v) => setSmtp({ host: v })} testid="settings-smtp_host" />
              <Text label="Porta" value={String(data.smtp.port)} onChange={(v) => setSmtp({ port: Number(v) || 587 })} testid="settings-smtp_port" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Text label="Username" value={data.smtp.username ?? ""} onChange={(v) => setSmtp({ username: v })} testid="settings-smtp_username" />
              <Text label="Password" value={data.smtp.password ?? ""} onChange={(v) => setSmtp({ password: v })} testid="settings-smtp_password" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Text label="From email" value={data.smtp.from_email ?? ""} onChange={(v) => setSmtp({ from_email: v })} testid="settings-smtp_from_email" />
              <Text label="From name" value={data.smtp.from_name} onChange={(v) => setSmtp({ from_name: v })} testid="settings-smtp_from_name" />
            </div>
            <Text label="Email notifiche admin" value={data.smtp.admin_notification_email ?? ""} onChange={(v) => setSmtp({ admin_notification_email: v })} testid="settings-smtp_admin_email" />
            <div className="flex items-center justify-between border border-white/10 px-4 py-3">
              <span className="text-sm">Usa TLS</span>
              <button
                type="button"
                data-testid="settings-smtp_use_tls"
                onClick={() => setSmtp({ use_tls: !data.smtp.use_tls })}
                className={`relative h-6 w-11 transition-colors ${data.smtp.use_tls ? "bg-secondary" : "bg-white/15"}`}
              >
                <span className={`absolute top-1 h-4 w-4 bg-black transition-all ${data.smtp.use_tls ? "left-6" : "left-1"}`} />
              </button>
            </div>

            <div className="border-t border-white/10 pt-6">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Test invio</p>
              <p className="text-xs text-muted-foreground mb-3">Salva prima le impostazioni, poi invia un'email di prova per verificare la configurazione.</p>
              <div className="flex gap-3">
                <input
                  data-testid="smtp-test-email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="destinatario@email.com"
                  className="flex-1 border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
                />
                <button
                  data-testid="smtp-test-button"
                  onClick={sendTest}
                  disabled={testing}
                  className="shrink-0 flex items-center gap-2 border border-secondary text-secondary font-bold uppercase tracking-[0.12em] text-xs px-5 py-2.5 hover:bg-secondary hover:text-black transition-colors disabled:opacity-60"
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia test
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

const Text: React.FC<{ label: string; value: string; onChange: (v: string) => void; testid: string }> = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">{label}</label>
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
    />
  </div>
);

const LegalField: React.FC<{ label: string; value: string; onChange: (v: string) => void; testid: string }> = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">{label}</label>
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={8}
      placeholder="Incolla qui il testo del documento…"
      className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
    />
  </div>
);
